import {forwardRef} from "react";
import {Link} from "react-router-dom";
import {rewriteFileUrlForExperimental, toInternalPath} from "../utils/dibUrls";

/**
 * Resolves a dib URL for in-app use, unconditionally: legacy file URLs are
 * rewritten to the same-origin proxy shape and app.dib.no URLs are converted
 * to internal SPA routes. Single home for the rewrite-then-internalize step
 * (also consumed by GPTStreamParser) — rendering stays per-consumer.
 */
export function useExperimentalLinkResolution(href: string): {
    effectiveHref: string;
    internalPath: string | null;
} {
    const effectiveHref = rewriteFileUrlForExperimental(href);
    const internalPath = toInternalPath(effectiveHref);
    return {effectiveHref, internalPath};
}

type ExperimentalLinkProps = {
    href: string;
    /** Suggested filename for same-origin file hrefs; renders a download
     *  anchor instead of a new-tab link (pattern: ResultRow). */
    download?: string;
    className?: string;
    children: React.ReactNode;
    transition?: boolean;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export const ExperimentalLink = forwardRef<
    HTMLAnchorElement,
    ExperimentalLinkProps
>(({href, download, className, children, transition = true, ...rest}, ref) => {
    const {effectiveHref, internalPath} = useExperimentalLinkResolution(href);

    return internalPath ? (
        <Link
            ref={ref}
            to={internalPath}
            viewTransition={transition}
            className={className}
            {...rest}
        >
            {children}
        </Link>
    ) : (
        <a
            ref={ref}
            href={effectiveHref}
            {...(download !== undefined
                ? {download, "data-pw": "file-download"}
                : {target: "_blank", rel: "noopener noreferrer"})}
            className={className}
            {...rest}
        >
            {children}
        </a>
    );
});

ExperimentalLink.displayName = "ExperimentalLink";
